import { api } from "@/lib/api/client";
import type { CalendarSubscribeClient, SharedCalendars } from "@/lib/google-calendar-subscribe";

/**
 * This person's place in the course's Google Calendar (ADR 0040). IMAM owns the
 * calendar through the school's Workspace and shares it read-only with the
 * person's school account; Google shows it, live, wherever they use Calendar.
 *
 * `no_account`: the person has no active school Google account to share with.
 * `available`: one click adds it. `added`: shared; `link` opens it in Google.
 * A 404 means the course offers no calendar to this seat, so nothing is drawn.
 * `subscribe`: when the school has a browser client, the press adds the
 * calendar to the person's list itself instead of opening Google's add page.
 */
export interface CourseGoogleCalendar {
  state: "no_account" | "available" | "added";
  added_at: string | null;
  link: string | null;
  calendar_id: string | null;
  subscribe: CalendarSubscribeClient | null;
}

/**
 * Every course calendar this person may read. `courses: 0` draws nothing;
 * `added` counts the ones already shared with the school account.
 */
export interface MyCourseCalendars {
  state: "no_account" | "available";
  courses: number;
  added: number;
  subscribe: CalendarSubscribeClient | null;
}

export type SharedMyCourseCalendars = MyCourseCalendars & SharedCalendars;

export const googleCalendarApi = {
  get: (courseId: string) => api.get<CourseGoogleCalendar>(`/courses/${courseId}/google-calendar`),
  /** Shares the course calendar with this person's school account; repeating it shares nothing twice. */
  add: (courseId: string) => api.post<CourseGoogleCalendar>(`/courses/${courseId}/google-calendar`),
  mine: () => api.get<MyCourseCalendars>("/me/course-calendars"),
  /** Shares every readable course calendar; the answer names the ones ready to add. */
  shareMine: () => api.post<SharedMyCourseCalendars>("/me/course-calendars/share"),
};
